import React, { useState } from 'react';
import { Form, Button, Card, Container, Row, Col, Alert, Tabs, Tab, Modal } from 'react-bootstrap';

import { leadService } from '../../services/firebaseService';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';

const LeadForm = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    mobileNo: '',
    source: '',
    budget: '',
    notes: '',
    leadType: '',
    propertyType: '',
    sector: '',
    project: '',
    floor: '',
    status: 'New'
  });
  const [importData, setImportData] = useState('');
  const [loading, setLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [activeTab, setActiveTab] = useState('single');
  const [phoneValidation, setPhoneValidation] = useState({ isValid: true, message: '' });
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // ✅ Enhanced Phone validation for worldwide numbers
  const isValidPhone = (phone) => {
    if (!phone || typeof phone !== 'string') return false;
    
    // Basic cleaning
    const cleaned = phone.trim();
    
    // Check for empty string after cleaning
    if (!cleaned) return false;
    
    // Enhanced international phone number validation
    // Basic pattern for international numbers: optional +, country code (1-3 digits), and phone number
    const internationalPattern = /^\+?[1-9]\d{0,2}[\s\-]?\(?\d{1,4}\)?[\s\-]?\d{1,4}[\s\-]?\d{1,4}[\s\-]?\d{1,9}$/;
    
    // Remove all non-digit characters for length check
    const digitsOnly = cleaned.replace(/\D/g, '');
    
    // Minimum length check (including country code)
    if (digitsOnly.length < 7) return false;
    
    // Maximum length check (international numbers typically up to 15 digits)
    if (digitsOnly.length > 15) return false;
    
    // Check if it matches the international pattern
    if (!internationalPattern.test(cleaned)) return false;
    
    // Additional validation for specific country formats
    return validateSpecificCountries(cleaned);
  };

  // ✅ Validate specific country formats for better accuracy
  const validateSpecificCountries = (phone) => {
    const digits = phone.replace(/\D/g, '');
    
    // Pakistan
    if (digits.startsWith('92') || phone.includes('+92') || (digits.startsWith('0') && digits.length === 11)) {
      return isValidPakistaniNumber(phone);
    }
    
    // USA/Canada
    if (digits.startsWith('1') || phone.includes('+1')) {
      return digits.length === 11; // 1 + 10 digits
    }
    
    // UK
    if (digits.startsWith('44') || phone.includes('+44')) {
      const ukDigits = digits.startsWith('44') ? digits.substring(2) : digits.substring(3);
      return ukDigits.length >= 9 && ukDigits.length <= 10;
    }
    
    // UAE
    if (digits.startsWith('971') || phone.includes('+971')) {
      const uaeDigits = digits.startsWith('971') ? digits.substring(3) : digits.substring(4);
      return uaeDigits.length === 9;
    }
    
    // Saudi Arabia
    if (digits.startsWith('966') || phone.includes('+966')) {
      const saDigits = digits.startsWith('966') ? digits.substring(3) : digits.substring(4);
      return saDigits.length === 9;
    }
    
    // India
    if (digits.startsWith('91') || phone.includes('+91')) {
      const indiaDigits = digits.startsWith('91') ? digits.substring(2) : digits.substring(3);
      return indiaDigits.length === 10;
    }
    
    // Generic validation for other countries
    // Country code (1-3 digits) + phone number (4-12 digits)
    const totalDigits = digits.length;
    return totalDigits >= 7 && totalDigits <= 15;
  };

  // ✅ Pakistani number validation (fixed missing function)
  const isValidPakistaniNumber = (phone) => {
    if (!phone) return false;
    
    // Remove all non-digit characters
    const digits = phone.replace(/\D/g, '');
    
    // Handle different Pakistani formats
    let pakistaniDigits;
    
    if (digits.startsWith('92') && digits.length === 12) {
      pakistaniDigits = digits.substring(2); // Remove 92
    } else if (digits.startsWith('0') && digits.length === 11) {
      pakistaniDigits = digits.substring(1); // Remove leading 0
    } else if (digits.length === 10) {
      pakistaniDigits = digits; // Already in correct format
    } else {
      return false;
    }
    
    // Check length
    if (pakistaniDigits.length !== 10) return false;
    
    // Pakistani mobile prefixes
    const validPrefixes = [
      '30', '31', '32', '33', '34', '35', '36', '37', // Jazz, Warid
      '40', '41', '42', '43', '44', '45', '46', '47', // Telenor
      '50', '51', '52', '53', '54', '55', '56', '57', // Zong
      '60', '61', '62', '63', '64', '65', '66', '67', // Ufone
      '70', '71', '72', '73', '74', '75', '76', '77', // SCO
      '90', '91', '92', '93', '94', '95', '96', '97'  // Others
    ];
    
    const prefix = pakistaniDigits.substring(0, 2);
    if (!validPrefixes.includes(prefix)) {
      return false;
    }
    
    // Check if it's a valid number (not all zeros, not repeating same digit, etc.)
    if (/^(\d)\1+$/.test(pakistaniDigits)) { // All same digits
      return false;
    }
    
    // Check for common invalid patterns
    if (/^0+$/.test(pakistaniDigits)) { // All zeros
      return false;
    }
    
    return true;
  };

  // ✅ Enhanced Normalize phone number for worldwide numbers
  const normalizePhone = (phone) => {
    if (!phone) return '';
    
    // Remove all non-digit characters except + at the beginning
    let cleaned = phone.trim();
    
    // If starts with +, keep it, otherwise remove all non-digits
    if (cleaned.startsWith('+')) {
      const plus = '+';
      const rest = cleaned.substring(1).replace(/\D/g, '');
      cleaned = plus + rest;
    } else {
      cleaned = cleaned.replace(/\D/g, '');
      // Add + if it's a long number (likely international)
      if (cleaned.length > 10 && !cleaned.startsWith('0')) {
        cleaned = '+' + cleaned;
      }
    }
    
    return cleaned;
  };

  // ✅ Enhanced phone validation with specific error messages for worldwide numbers
  const validatePhoneInRealTime = (phone) => {
    if (!phone) {
      setPhoneValidation({ isValid: true, message: '' });
      return true;
    }
    
    const cleaned = phone.trim();
    
    if (!cleaned) {
      setPhoneValidation({ isValid: false, message: 'Please enter a mobile number' });
      return false;
    }
    
    if (!isValidPhone(phone)) {
      setPhoneValidation({ 
        isValid: false, 
        message: 'Please enter a valid international mobile number\n\nExamples:\n• +1-555-123-4567 (USA/Canada)\n• +44 20 7946 0958 (UK)\n• +971 50 123 4567 (UAE)\n• +92 334 7600608 (Pakistan)\n• +91 98765 43210 (India)' 
      });
      return false;
    }
    
    setPhoneValidation({ isValid: true, message: '' });
    return true;
  };

  // ✅ Enhanced duplicate checking with worldwide number support
  const isMobileNumberExists = async (mobileNo) => {
    if (!mobileNo) return { exists: false, existingLead: null };
    
    try {
      const normalizedPhone = normalizePhone(mobileNo);
      
      // Additional validation for the normalized phone
      if (!isValidPhone(normalizedPhone)) {
        return { 
          exists: false, 
          existingLead: null, 
          normalizedPhone,
          error: 'Invalid phone number format'
        };
      }
      
      const leads = await leadService.getLeadsByMobileNumber(normalizedPhone);
      
      if (leads && leads.length > 0) {
        return { 
          exists: true, 
          existingLead: leads[0],
          normalizedPhone 
        };
      }
      
      // Also check without + for numbers that might be stored differently
      if (normalizedPhone.startsWith('+')) {
        const withoutPlus = normalizedPhone.substring(1);
        const leadsWithoutPlus = await leadService.getLeadsByMobileNumber(withoutPlus);
        
        if (leadsWithoutPlus && leadsWithoutPlus.length > 0) {
          return { 
            exists: true, 
            existingLead: leadsWithoutPlus[0],
            normalizedPhone 
          };
        }
      } else {
        // Check with + if number doesn't have it
        const withPlus = '+' + normalizedPhone;
        const leadsWithPlus = await leadService.getLeadsByMobileNumber(withPlus);
        
        if (leadsWithPlus && leadsWithPlus.length > 0) {
          return { 
            exists: true, 
            existingLead: leadsWithPlus[0],
            normalizedPhone 
          };
        }
      }
      
      return { exists: false, existingLead: null, normalizedPhone };
    } catch (error) {
      console.error('Error checking mobile number:', error);
      return { exists: false, existingLead: null, normalizedPhone: normalizePhone(mobileNo) };
    }
  };

  // ✅ Enhanced imported lead validation for worldwide numbers
  const validateImportedLead = (row, rowIndex) => {
    const errors = [];
    
    // Check minimum required fields
    if (!row[0]?.trim()) {
      errors.push(`Row ${rowIndex + 1}: Full Name is required`);
    }
    
    if (!row[2]?.trim()) {
      errors.push(`Row ${rowIndex + 1}: Mobile Number is required`);
    } else if (!isValidPhone(row[2])) {
      errors.push(`Row ${rowIndex + 1}: Invalid international mobile number format "${row[2]}"`);
    }
    
    if (!row[6]?.trim()) {
      errors.push(`Row ${rowIndex + 1}: Lead Type is required`);
    }
    
    if (!row[7]?.trim()) {
      errors.push(`Row ${rowIndex + 1}: Property Type is required`);
    }
    
    // Validate property type specific fields
    if (row[7] === 'plot' && !row[8]?.trim()) {
      errors.push(`Row ${rowIndex + 1}: Sector is required for Plot property type`);
    }
    
    if (row[7] === 'project-unit' && !row[9]?.trim()) {
      errors.push(`Row ${rowIndex + 1}: Project is required for Project Unit property type`);
    }
    
    return errors;
  };

  // ✅ Show error modal
  const showErrorPopup = (message) => {
    setModalMessage(message);
    setShowErrorModal(true);
  };

  // ✅ Show success modal
  const showSuccessPopup = (message) => {
    setModalMessage(message);
    setShowSuccessModal(true);
  };

  // ✅ Handle modal close
  const handleModalClose = () => {
    setShowErrorModal(false);
    setShowSuccessModal(false);
    setModalMessage('');
  };

  // Project options
  const projects = {
    'one-destination': 'One Destination',
    'business-hub': 'Business Hub',
    'gvr': 'GVR',
    'bodla-homes': 'Bodla Homes'
  };

  const floorOptions = [
    'Ground Floor',
    'First Floor',
    'Second Floor',
    'Third Floor',
    'Fourth Floor',
    'Fifth Floor'
  ];

  const dhaSectors = [
    "Rumanza", "Sector A", "Sector B1", "Sector D", "Sector E",
    "Sector E1", "Sector E2", "Sector F", "Sector G", "Sector H",
    "Sector I", "Sector K", "Sector L", "Sector M", "Sector N",
    "Sector O", "Sector P", "Sector Q", "Sector R", "Sector S",
    "Sector T", "Sector U", "Sector V", "Sector W1", "Sector W2",
    "Sector X", "Sector Y","Other",
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));

    // Real-time phone validation
    if (name === 'mobileNo') {
      validatePhoneInRealTime(value);
    }
  };

  const handleImportChange = (e) => {
    setImportData(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Clear previous messages
    setError('');
    setSuccess('');

    if (!currentUser) {
      showErrorPopup('You must be logged in to create a lead');
      return;
    }

    // Validate required fields
    if (!formData.fullName?.trim()) {
      showErrorPopup('Full Name is required');
      return;
    }

    if (!formData.mobileNo) {
      showErrorPopup('Mobile number is required');
      return;
    }

    if (!isValidPhone(formData.mobileNo)) {
      showErrorPopup('Please enter a valid international mobile number\n\nAccepted formats:\n• +1-555-123-4567 (USA/Canada)\n• +44 20 7946 0958 (UK)\n• +971 50 123 4567 (UAE)\n• +92 334 7600608 (Pakistan)\n• +91 98765 43210 (India)\n• Other international formats');
      return;
    }

    if (!formData.leadType) {
      showErrorPopup('Lead Type is required');
      return;
    }

    if (!formData.propertyType) {
      showErrorPopup('Property Type is required');
      return;
    }

    // Property type specific validation
    if (formData.propertyType === 'plot' && !formData.sector) {
      showErrorPopup('Sector is required for Plot property type');
      return;
    }

    if (formData.propertyType === 'project-unit' && !formData.project) {
      showErrorPopup('Project is required for Project Unit property type');
      return;
    }

    setLoading(true);

    try {
      // ✅ Enhanced duplicate check with worldwide number support
      const { exists, existingLead, normalizedPhone, error } = await isMobileNumberExists(formData.mobileNo);
      
      if (error) {
        showErrorPopup(`Invalid mobile number format: ${error}`);
        setLoading(false);
        return;
      }
      
      if (exists && existingLead) {
        showErrorPopup(
          `This mobile number is already associated with another lead:\n\n` +
          `Existing Lead: ${existingLead.fullName}\n` +
          `Mobile: ${existingLead.mobileNo}\n` +
          `Created: ${new Date(existingLead.createdAt?.toDate()).toLocaleDateString()}\n\n` +
          `Please use a different mobile number.`
        );
        setLoading(false);
        return;
      }

      const leadData = {
        ...formData,
        mobileNo: normalizedPhone, // Store normalized phone number
        agentId: currentUser.uid,
        agentName: currentUser.displayName || currentUser.email,
        createdAt: new Date()
      };

      await leadService.createLead(leadData);
      
      // Show success popup
      showSuccessPopup('Lead added successfully! You will be redirected to leads page shortly.');
      
      // Reset form
      setFormData({
        fullName: '',
        email: '',
        mobileNo: '',
        source: '',
        budget: '',
        notes: '',
        leadType: '',
        propertyType: '',
        sector: '',
        project: '',
        floor: '',
        status: 'New'
      });

      // Reset phone validation
      setPhoneValidation({ isValid: true, message: '' });

      // Redirect after 3 seconds
      setTimeout(() => {
        navigate('/leads');
      }, 3000);

    } catch (err) {
      showErrorPopup('Error adding lead: ' + err.message);
    }

    setLoading(false);
  };

  const handleImport = async () => {
    if (!currentUser) {
      showErrorPopup('You must be logged in to import leads');
      return;
    }

    if (!importData.trim()) {
      showErrorPopup('Please enter lead data to import');
      return;
    }

    setImportLoading(true);
    setError('');
    setSuccess('');

    try {
      const lines = importData.split('\n').filter(line => line.trim());
      const importedLeads = [];
      const duplicatePhones = [];
      const invalidRows = [];
      const systemDuplicates = [];
      const processedPhones = new Set(); // Track processed numbers in this import

      // ✅ Skip header row if detected
      const startIndex = lines[0].toLowerCase().includes('mobileno') || 
                        lines[0].toLowerCase().includes('fullname') ? 1 : 0;

      // First pass: validate all rows
      for (let i = startIndex; i < lines.length; i++) {
        const line = lines[i];
        const parts = line.split(',').map(part => part ? part.toString().trim() : '');

        if (parts.length >= 3) {
          // Validate the row
          const validationErrors = validateImportedLead(parts, i - startIndex);
          
          if (validationErrors.length > 0) {
            invalidRows.push(...validationErrors);
            continue;
          }

          const phone = parts[2];
          const normalizedPhone = normalizePhone(phone);

          // Additional validation for normalized phone
          if (!isValidPhone(normalizedPhone)) {
            invalidRows.push(`Row ${i - startIndex + 1}: Invalid international mobile number "${phone}"`);
            continue;
          }

          // Check for duplicates within the same import file
          if (processedPhones.has(normalizedPhone)) {
            duplicatePhones.push({ phone, row: i - startIndex + 1, name: parts[0] });
            continue; // Skip this lead (duplicate in same file)
          }

          // Check if this phone already exists in the system
          const { exists } = await isMobileNumberExists(normalizedPhone);
          if (exists) {
            systemDuplicates.push({ phone, row: i - startIndex + 1, name: parts[0] });
            continue; // Skip this lead (exists in database)
          }

          processedPhones.add(normalizedPhone);

          const leadData = {
            fullName: parts[0] || '',
            email: parts[1] || '',
            mobileNo: normalizedPhone,
            source: parts[3] || '',
            budget: parts[4] || '',
            notes: parts[5] || '',
            leadType: parts[6] || '',
            propertyType: parts[7] || '',
            sector: parts[8] || '',
            project: parts[9] || '',
            floor: parts[10] || '',
            status: 'New',
            agentId: currentUser.uid,
            agentName: currentUser.displayName || currentUser.email,
            createdAt: new Date()
          };

          importedLeads.push(leadData);
        } else {
          invalidRows.push(`Row ${i - startIndex + 1}: Insufficient data columns (need at least 3 columns)`);
        }
      }

      // Show validation errors if any
      if (invalidRows.length > 0) {
        showErrorPopup(
          `Validation errors found:\n\n${invalidRows.slice(0, 10).join('\n')}${
            invalidRows.length > 10 ? `\n...and ${invalidRows.length - 10} more errors` : ''
          }`
        );
        setImportLoading(false);
        return;
      }

      if (importedLeads.length === 0) {
        let errorMessage = 'No valid leads to import.';
        
        if (duplicatePhones.length > 0 || systemDuplicates.length > 0) {
          errorMessage += '\n\nReasons:';
          if (duplicatePhones.length > 0) {
            errorMessage += `\n• ${duplicatePhones.length} duplicate numbers in import file`;
          }
          if (systemDuplicates.length > 0) {
            errorMessage += `\n• ${systemDuplicates.length} numbers already exist in system`;
          }
        }
        
        showErrorPopup(errorMessage);
        setImportLoading(false);
        return;
      }

      // Second pass: create the leads with individual error handling
      let createdCount = 0;
      const creationErrors = [];

      for (const lead of importedLeads) {
        try {
          await leadService.createLead(lead);
          createdCount++;
        } catch (error) {
          console.error(`Error creating lead ${lead.mobileNo}:`, error);
          creationErrors.push(`Failed to create lead ${lead.fullName} (${lead.mobileNo}): ${error.message}`);
        }
      }

      // Prepare success message with detailed breakdown
      let successMessage = `Successfully imported ${createdCount} out of ${importedLeads.length} leads!`;
      
      if (duplicatePhones.length > 0) {
        successMessage += `\n\n ${duplicatePhones.length} duplicates skipped from import file`;
      }
      
      if (systemDuplicates.length > 0) {
        successMessage += `\n\n ${systemDuplicates.length} duplicates skipped (already exist in system)`;
      }
      
      if (creationErrors.length > 0) {
        successMessage += `\n\n Creation errors:\n${creationErrors.slice(0, 3).join('\n')}${
          creationErrors.length > 3 ? `\n...and ${creationErrors.length - 3} more errors` : ''
        }`;
      }

      showSuccessPopup(successMessage);
      setImportData('');
      
      // Redirect after 3 seconds if successful
      if (createdCount > 0) {
        setTimeout(() => {
          navigate('/leads');
        }, 3000);
      }

    } catch (err) {
      showErrorPopup('Error importing leads: ' + err.message);
    }

    setImportLoading(false);
  };

  const handleFileImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      if (file.name.endsWith('.csv') || file.name.endsWith('.txt')) {
        setImportData(event.target.result);
      } else {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const json = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false });
        const csvData = json.map(row => row.join(',')).join('\n');
        setImportData(csvData);
      }
    };

    if (file.name.endsWith('.csv') || file.name.endsWith('.txt')) {
      reader.readAsText(file);
    } else {
      reader.readAsArrayBuffer(file);
    }
  };

  const downloadTemplate = () => {
    const template = `FullName,Email,MobileNo,Source,Budget,Notes,LeadType,PropertyType,Sector,Project,Floor
Ali Khan,ali.khan@example.com,+923347600608,Website,5000000,Interested in buying a plot,property-sale,plot,Sector A,,
John Smith,john.smith@example.com,+15551234567,Referral,7500000,Looking for investment,property-sale,project-unit,,One Destination,First Floor
Fatima Ahmed,fatima.ahmed@example.com,+971501234567,Facebook,6000000,Interested in shop booking,booking,shop,,Mall of Dubai,Ground Floor
David Brown,david.brown@example.com,+442079460958,Walk-in,12000000,Wants to invest in apartments,property-sale,apartment,Sector B,Bahria Town,Second Floor
Priya Sharma,priya.sharma@example.com,+919876543210,Instagram,8500000,Looking for commercial space,property-sale,project-unit,,Business Hub,Third Floor`;

    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'leads_template_international.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const showFloor = formData.propertyType === 'project-unit' &&
    formData.project &&
    ['one-destination', 'business-hub', 'gvr'].includes(formData.project);

  return (
    <Container className='mt-4'>
      <Row className="justify-content-center">
        <Col md={10}>
          <Card>
            <Card.Body>
              <h2 className="text-center mb-4">Add Leads to Your Profile</h2>

              <Tabs
                activeKey={activeTab}
                onSelect={(tab) => setActiveTab(tab)}
                className="mb-3"
              >
                {/* Single Lead Form */}
                <Tab eventKey="single" title="Single Lead">
                  <Form onSubmit={handleSubmit}>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Full Name *</Form.Label>
                          <Form.Control
                            type="text"
                            name="fullName"
                            value={formData.fullName}
                            onChange={handleChange}
                            required
                            placeholder="Enter full name"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Email</Form.Label>
                          <Form.Control
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="Enter email address (optional)"
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Mobile No *</Form.Label>
                          <Form.Control
                            type="tel"
                            name="mobileNo"
                            value={formData.mobileNo}
                            onChange={handleChange}
                            required
                            placeholder="Enter international mobile number"
                            isInvalid={!phoneValidation.isValid && formData.mobileNo.length > 0}
                          />
                          {!phoneValidation.isValid && formData.mobileNo.length > 0 && (
                            <Form.Control.Feedback type="invalid">
                              <span style={{ whiteSpace: 'pre-line' }}>{phoneValidation.message}</span>
                            </Form.Control.Feedback>
                          )}
                          <Form.Text className="text-muted">
                            Mobile number must be unique across all leads. 
                          </Form.Text>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Source</Form.Label>
                          <Form.Select name="source" value={formData.source} onChange={handleChange}>
                            <option value="">Select Source</option>
                            <option value="Website">Website</option>
                            <option value="Referral">Referral</option>
                            <option value="Walk-in">Walk-in</option>
                            <option value="Advertisement">Advertisement</option>
                            <option value="Social Media">Social Media</option>
                            <option value="Import">Import</option>
                            <option value="International">International</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Budget</Form.Label>
                          <Form.Control
                            type="number"
                            name="budget"
                            value={formData.budget}
                            onChange={handleChange}
                            placeholder="Enter budget amount"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Lead Type *</Form.Label>
                          <Form.Select
                            name="leadType"
                            value={formData.leadType}
                            onChange={handleChange}
                            required
                          >
                            <option value="">Select Lead Type</option>
                            <option value="property-sale">Property Sale</option>
                            <option value="property-purchase">Property Purchase</option>
                            <option value="construction">Construction</option>
                            <option value="rent">Rent</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Property Type *</Form.Label>
                          <Form.Select
                            name="propertyType"
                            value={formData.propertyType}
                            onChange={handleChange}
                            required
                          >
                            <option value="">Select Property Type</option>
                            <option value="plot">Plot</option>
                            <option value="project-unit">Project Unit</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        {formData.propertyType === 'plot' && (
                          <Form.Group className="mb-3">
                            <Form.Label>Sector *</Form.Label>
                            <Form.Select
                              name="sector"
                              value={formData.sector}
                              onChange={handleChange}
                              required
                            >
                              <option value="">Select Sector</option>
                              {dhaSectors.map((sector, index) => (
                                <option key={index} value={sector}>{sector}</option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        )}

                        {formData.propertyType === 'project-unit' && (
                          <Form.Group className="mb-3">
                            <Form.Label>Project *</Form.Label>
                            <Form.Select
                              name="project"
                              value={formData.project}
                              onChange={handleChange}
                              required
                            >
                              <option value="">Select Project</option>
                              {Object.entries(projects).map(([key, value]) => (
                                <option key={key} value={key}>{value}</option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        )}
                      </Col>
                    </Row>

                    {showFloor && (
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Floor</Form.Label>
                            <Form.Select
                              name="floor"
                              value={formData.floor}
                              onChange={handleChange}
                            >
                              <option value="">Select Floor</option>
                              {floorOptions.map((floor, index) => (
                                <option key={index} value={floor.toLowerCase().replace(' ', '-')}>
                                  {floor}
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                      </Row>
                    )}

                    <Form.Group className="mb-3">
                      <Form.Label>Notes / Requirements</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="notes"
                        value={formData.notes}
                        onChange={handleChange}
                        placeholder="Enter any additional notes or requirements..."
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Status</Form.Label>
                      <Form.Select name="status" value={formData.status} onChange={handleChange}>
                        <option value="New">New</option>
                        <option value="Contacted">Contacted</option>
                        <option value="Visited">Visited</option>
                        <option value="Qualified">Qualified</option>
                        <option value="Closed">Closed</option>
                        <option value="Lost">Lost</option>
                      </Form.Select>
                    </Form.Group>

                    <Button disabled={loading || !phoneValidation.isValid} type="submit" className="w-100" variant="primary">
                      {loading ? 'Adding Lead...' : 'Add to My Leads'}
                    </Button>
                  </Form>
                </Tab>

                {/* Import Leads */}
                <Tab eventKey="import" title="Import Multiple Leads">
                  <Alert variant="info" className="mb-3">
                    <strong>🌍 Worldwide Phone Support:</strong> Now accepts international numbers from all countries<br />
                    <strong>📋 Import Format:</strong> CSV or Excel (.csv, .txt, .xlsx, .xls)<br />
                    <strong>✅ Required Columns:</strong> FullName, MobileNo, LeadType, PropertyType, (Sector for Plot) or (Project for Project Unit)<br />
                    <strong>📱 Phone Validation:</strong> Accepts international formats with country codes<br />
                    <strong>⚠️ Auto-Rejection:</strong> Duplicate numbers (any format) are automatically skipped
                  </Alert>

                  <Row className="mb-3">
                    <Col>
                      <Form.Group>
                        <Form.Label>Upload CSV or Excel File</Form.Label>
                        <Form.Control
                          type="file"
                          accept=".csv,.txt,.xlsx,.xls"
                          onChange={handleFileImport}
                        />
                        <Form.Text className="text-muted">
                          Or download the template below and upload your file
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col>
                      <Button variant="outline-primary" onClick={downloadTemplate}>
                        🌍 Download International CSV Template
                      </Button>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>Or paste CSV data here:</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={10}
                      value={importData}
                      onChange={handleImportChange}
                      placeholder="FullName,Email,MobileNo,Source,Budget,Notes,LeadType,PropertyType,Sector,Project,Floor"
                    />
                  </Form.Group>

                  <Button
                    disabled={importLoading || !importData.trim()}
                    onClick={handleImport}
                    className="w-100"
                    variant="primary"
                  >
                    {importLoading ? 'Checking and Importing Leads...' : `Import Leads (${importData.split('\n').filter(line => line.trim()).length} rows found)`}
                  </Button>
                </Tab>
              </Tabs>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Error Modal */}
      <Modal show={showErrorModal} onHide={handleModalClose} centered size="lg">
        <Modal.Header closeButton className="bg-danger text-white">
          <Modal.Title>Error</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex align-items-center">
            <i className="fas fa-exclamation-circle text-danger me-3 fs-4"></i>
            <span style={{ whiteSpace: 'pre-line' }}>{modalMessage}</span>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleModalClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Success Modal */}
      <Modal show={showSuccessModal} onHide={handleModalClose} centered size="lg">
        <Modal.Header closeButton className="bg-success text-white">
          <Modal.Title>✅ Success</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex align-items-center">
            <i className="fas fa-check-circle text-success me-3 fs-4"></i>
            <span style={{ whiteSpace: 'pre-line' }}>{modalMessage}</span>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="success" onClick={handleModalClose}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default LeadForm;